// Show / hide modules and actions by the logged-in admin's permissions (Hanko admin portal).
//
// admin-express returns user.permissions with the login answer (/api/login, /api/login/twofa) and with
// /get/fresh/user/data: the comma separated permission names of the admin's roles
// (roles -> roles_has_permission -> permissions, status 1). {} means the role has no permission.
// Matching is by WHOLE name, the same rule as admin-express (req-input-validator-ext isAllowed),
// so e.g. CLIENT_VIEW_OTP does not give CLIENT_VIEW.

export const NO_PERMISSION_MESSAGE = "You don't have permission to access this route."

// API path -> permission admin-express checks for it (app/express/plugins/*/routes/validator).
// The portal does not even send these requests without the permission.
export const ENDPOINT_PERMISSIONS = {
	'/admin/get/admins/list': 'ADMIN_LIST',
	'/admin/get/admins/details': 'ADMIN_VIEW',
	'/admin/update/admin': 'ADMIN_UPDATE',
	'/admin/verify/admin': 'ADMIN_VERIFY',
	'/admin/disable/admin': 'ADMIN_DISABLE',
	'/admin/add/bonus': 'BONUS_CREATE',
	'/admin/disable/bonus': 'BONUS_CREATE',
	'/admin/get/allowed/bonus': 'BONUS_CREATE',
	'/admin/update/bonus': 'BONUS_UPDATE',
	'/admin/get/bonus/list': 'BONUS_LIST',
	'/admin/get/bonus/details': 'BONUS_VIEW',
	'/admin/get/deposit/list': 'DEPOSIT_LIST',
	'/admin/get/deposit/details': 'DEPOSIT_VIEW',
	'/admin/update/deposit/detail': 'DEPOSIT_UPDATE',
	'/admin/add/deposit/detail': 'DEPOSIT_CREATE',
	'/admin/deposit/update': 'DEPOSIT_UPDATE',
	'/admin/list/ticket': 'TICKET_LIST',
	'/admin/ticket/details': 'TICKET_VIEW',
	'/admin/create/ticket': 'TICKET_CREATE',
	'/admin/get/trading/list': 'TRADING_LIST',
	'/admin/get/trading/details': 'TRADING_VIEW',
	'/admin/create/account': 'TRADING_CREATE',
	'/admin/get/user/details': 'CLIENT_VIEW',
	'/admin/get/user/list': 'CLIENT_LIST',
	'/admin/create/user': 'CLIENT_CREATE',
	'/admin/verify/user': 'CLIENT_VERIFY',
	'/admin/disable/user': 'CLIENT_DISABLE',
	'/admin/get/withdrawal/list': 'WITHDRAWAL_LIST',
	'/admin/get/withdrawal/details': 'WITHDRAWAL_VIEW',
	'/admin/withdrawal/request/otp': 'WITHDRAWAL_CREATE',
	'/admin/update/withdrawal/details': 'WITHDRAWAL_UPDATE'
}

// page -> permission(s); any one of them opens the page (create pages are also the edit pages)
export const ROUTE_PERMISSIONS = {
	'/admin-list': ['ADMIN_LIST'],
	'/admin-view': ['ADMIN_VIEW'],
	'/create-admin': ['ADMIN_CREATE'],
	'/listing': ['CLIENT_LIST'],
	'/view': ['CLIENT_VIEW'],
	'/create-client': ['CLIENT_CREATE'],
	'/master-listing': ['MASTER_LIST'],
	'/master-view': ['MASTER_LIST'],
	'/withdrawal-list': ['WITHDRAWAL_LIST'],
	'/withdrawal-view': ['WITHDRAWAL_VIEW'],
	'/create-withdrawal': ['WITHDRAWAL_CREATE', 'WITHDRAWAL_UPDATE'],
	'/deposit-list': ['DEPOSIT_LIST'],
	'/deposit-view': ['DEPOSIT_VIEW'],
	'/deposit-transaction': ['DEPOSIT_TRANSACTION'],
	'/create-deposit': ['DEPOSIT_CREATE', 'DEPOSIT_UPDATE'],
	'/bonus-list': ['BONUS_LIST'],
	'/bonus-view': ['BONUS_VIEW'],
	'/create-bonus': ['BONUS_CREATE', 'BONUS_UPDATE'],
	'/create-manual-bonus': ['BONUS_CREATE'],
	'/commission-list': ['COMMISSION_LIST'],
	'/commission-view': ['COMMISSION_VIEW'],
	'/create-commission': ['COMMISSION_CREATE'],
	'/trading-list': ['TRADING_LIST'],
	'/trading-view': ['TRADING_VIEW'],
	'/create-trading': ['TRADING_CREATE'],
	'/copytrading-list': ['TRADING_LIST'],
	'/copytrading-view': ['TRADING_VIEW'],
	'/createcopy-trading': ['TRADING_CREATE'],
	'/ticket-list': ['TICKET_LIST'],
	'/ticket-view': ['TICKET_VIEW'],
	'/create-ticket': ['TICKET_CREATE']
}

// list of permission names, or null while the permissions are not loaded yet (old session before re-login)
export const permissionList = (user) => {
	if (!user || typeof user !== 'object') { return null }
	const p = user.permissions
	if (typeof p === 'string') { return p.split(',').map(name => name.trim()).filter(name => name !== '') }
	if (Array.isArray(p)) { return p.map(name => String(name).trim()).filter(name => name !== '') }
	if (p && typeof p === 'object') { return [] } // {} = role without permissions
	return null
}

export const permissionsLoaded = (user) => permissionList(user) !== null

export const hasPermission = (user, name) => {
	const list = permissionList(user)
	return list !== null && typeof name === 'string' && list.indexOf(name.trim()) !== -1
}

export const hasAnyPermission = (user, names) => (names || []).some(name => hasPermission(user, name))

// the admin as saved by the login / reload actions (localStorage 'user', base64 JSON)
export const storedUser = () => {
	try {
		const stored = localStorage.getItem('user')
		return stored ? JSON.parse(atob(stored)) : null
	} catch (ex) {
		return null
	}
}

// true when the API needs a permission the logged-in admin does not have
// (permissions not loaded yet: the request goes out and admin-express decides)
export const isEndpointBlocked = (apiLink, user) => {
	const need = ENDPOINT_PERMISSIONS[apiLink]
	if (!need) { return false }
	const current = user || storedUser()
	return permissionsLoaded(current) && !hasPermission(current, need)
}

// true when the page needs a permission the logged-in admin does not have
export const isRouteBlocked = (path, user) => {
	const need = ROUTE_PERMISSIONS[path]
	if (!need) { return false }
	const current = user || storedUser()
	return permissionsLoaded(current) && !hasAnyPermission(current, need)
}



// WEBPACK FOOTER //
// ./src/store/api/permissions.js